// Dependencies
import { redirect, type Params } from 'react-router-dom';
import { ApolloQueryResult, FetchResult } from '@apollo/client';
import debug from 'debug';

// Types
import { Vehicle } from '../types';
import { OrderState, OrderType } from '../../../../../types/orders';
import { VehicleOwnership, VehicleUsage } from '../../../../../types/vehicles';

// Constants
import {
  directoryNaming,
  formAccidentNaming,
} from '../../../../../components/forms/AddBasicInfoAccidentForm/constants';

// GQL
import { client } from '../../../../../gql/client';
import { prependAccidentToCache } from '../../../../../gql/cacheHelpers';
// FOLD (At.0001979.0008.02): CREATE_NEW_IMAGE, DELETE_MANY_IMAGES,
// DELETE_ORDER_BY_ID, FIND_OR_CREATE_VEHICLE_ACCIDENT_CONTEXT y
// DELETE_ALL_VEHICLE_CONTEXTS ya no se usan — vehículos+contexts+fotos viajan
// dentro de SAVE_ACCIDENT (tx) y el rollback compensatorio se eliminó.
import {
  SAVE_ACCIDENT,
  CREATE_NEW_ATTENTION_ACCIDENT,
} from '../../../../../gql/mutations/accidents';

import { GET_ATTENTION_ACCIDENT_BY_ID } from '../../../../../gql/queries/accidents/attentionAccidents';
import { GET_ACCIDENT_BY_ID } from '../../../../../gql/queries/accidents/accidents';
import { GET_ORDER_BY_ID } from '../../../../../gql/queries/orders/orders';
import {
  GET_VEHICLE_BY_ID,
  GET_VEHICLE_CONTEXTS_BY_ACCIDENT,
} from '../../../../../gql/queries/vehicles';
import { FIND_OR_CREATE_VEHICLE_BY_PLATE } from '../../../../../gql/mutations/vehicles';

// Utils
import {
  UploadImage,
  CloudinaryUploadError,
} from '../../../../../utils/image';
import { saveFilesIntoADirectory } from '../../../../../utils/files';
import {
  addImageUploadOperation,
  OFFLINE_PENDING_UPLOADS_DIR,
} from '../../../../../offline/offlineImageUploadQueue';
import { getSketchFromSessionStorage } from '../../../../../utils/sketch';
import i18n from '../../../../../i18n';
import { getClassificationCode } from '../../../../../utils/orders';
import {
  compressSessionStorage,
  decompressSessionStorage,
} from '../../../../../utils/sessionStorage';
import { AccidentRoute } from '../../../../../types/route';
import {
  assertDraftOwner,
  DraftOwnerMismatchError,
} from '../../../../../utils/draftOwnership';
import {
  ACCIDENT_CREATION_SCOPE,
  accidentCreationOwnerId,
} from '../../../../../utils/draftScopes';
import {
  accidentCreationPhotoNames,
  accidentCreationSketch,
  readAccidentCreationCopy,
  releaseAccidentCreationDraft,
} from '../../../../../utils/accidentCreationDraft';
import { draftSketchFileName, pickSketchFile } from '../../../../../utils/sketchFile';
import { screenFormContext, type FormContext } from '../../../../../offline/formStorage';
import {
  tableIds,
  takeBase,
} from '../../../../../components/display/tables/types';
import { setCursor } from '../../../../../state/actions/ui/cursorTables';
import { store } from '../../../../../state/configureStore';

import { getCursor } from '../../../../../state/selectors/ui/tables';
// Sentry
import * as Sentry from '@sentry/react';

// Offline compound queue
import { queueAccidentCreate } from './accidentQueue';
import { isOnlineNow } from '../../../../../offline/connectivity';
import { isActuallyOnline } from '../../../../../offline/checkConnectivity';
import { createOfflineQueuedError } from '../../../../../offline/isOfflineQueuedError';
import {
  createPhaseDeadline,
  phaseScopedClient,
  raceWithDeadline,
  isTransportFailure,
} from '../../../../../offline/phase2Transport';
import { enqueuePhotosForUpload } from '../../../../../utils/enqueuePhotos';
import { formVehicleNaming } from '../../../../../components/forms/AddVehicleForm/constants';

// Configure debug namespaces
const logMain = debug('rois:accident:add');
const logVehicles = debug('rois:accident:add:vehicles');
const logError = debug('rois:accident:add:error');
const logContext = debug('rois:accident:add:context');

// Enable logs only in development
if (import.meta.env.DEV) {
  // You can use localStorage to activate all logs in development
  debug.enable('rois:accident:add:*');

  // Initialization messages
  logMain('Debug activado para módulo de creación de accidentes');
}

export const saveData = async (
  formData: FormData,
  accidentRouteInner: string,
  // Idempotencia: el replay offline (offlineCompoundSyncManager.syncAccident)
  // pasa un clientOpId ESTABLE — el del intento con red si lo hubo, o el
  // derivado de op.id — y un reintento de la cola no duplica el accidente.
  // Con red se genera UNA VEZ al entrar (`saveClientOpId`): si el create se
  // aborta por el plazo, viaja en la operación encolada (spec
  // 2026-09-08-fase2-encolada § 2 D3).
  //
  // `form`: de dónde leer y escribir el formulario. Por defecto, la pantalla
  // (`sessionStorage` y las carpetas OPFS); el reintento de la cola pasa uno en
  // memoria con los datos de la operación, para no tocar el formulario abierto
  // (spec outbox-autocontenido v2 § 5).
  // `params`: los de la ruta en el guardado en vivo (`:id` = la OT o campaña
  // PADRE de la URL). El reintento de la cola no los tiene ni los necesita.
  opts?: { clientOpId?: string; form?: FormContext; params?: Params }
) => {
  debug.enable('rois:accident:add:*');
  logMain('🔍 INICIANDO PROCESO DE CREACIÓN DE NUEVO ACCIDENTE');

  const isReplay = !!opts?.clientOpId;
  const form = opts?.form ?? screenFormContext;
  const saveClientOpId = opts?.clientOpId ?? crypto.randomUUID();

  // 🔴 Barrera (S4 de la auditoría de accidentes; invariante I3 del spec
  // 2026-11-02 wizard-draft-ownership): el accidente se crea bajo el padre de
  // la URL, nunca bajo el del borrador. Antes `parentOrderId` salía SOLO de
  // `new-accident-form.orderParentId`: con el alta de P1 a medias, la URL de
  // P2 creaba el accidente bajo P1. Dos comprobaciones, las dos solo en vivo
  // (el reintento de la cola trabaja con su copia en memoria y no gana
  // rechazos nuevos, `.claude/rules/guard-nuevo-cola-offline.md`):
  //  1. El borrador es de este padre (dueño que dejan los loaders).
  //  2. Hay `:id` en la URL y el padre del borrador es ESE. Sin `:id` se
  //     falla cerrado: sin él `assertDraftOwner` no comprueba nada y, con un
  //     borrador sin padre, `undefined === undefined` dejaba pasar el alta
  //     (revisión de la Task 9).
  // Va ANTES de la rama sin red: tampoco se encola un alta ajena. No debería
  // saltar nunca —los loaders descartan el borrador de otro padre y la
  // pantalla remonta—; si salta no se escribe NI se encola nada.
  const urlParentId = opts?.params?.id;
  assertDraftOwner(
    ACCIDENT_CREATION_SCOPE,
    accidentCreationOwnerId(urlParentId) ?? undefined,
    { isReplay },
    form.store
  );
  if (!isReplay) {
    const draftParentId = readAccidentCreationCopy(
      formAccidentNaming.NEW_ACCIDENT_FORM,
      form.store
    )?.orderParentId;
    if (!urlParentId || draftParentId !== urlParentId) {
      const mismatch = new DraftOwnerMismatchError(
        ACCIDENT_CREATION_SCOPE.name,
        typeof draftParentId === 'string' ? draftParentId : null,
        urlParentId ?? '(sin :id)'
      );
      Sentry.captureException(mismatch, {
        tags: { guard: `draft-owner-mismatch:${ACCIDENT_CREATION_SCOPE.name}` },
      });
      throw mismatch;
    }
  }

  // ── ENTRADA (spec 2026-09-08-fase2-encolada § 3.6) ──────────────────────
  // Fuente única de conectividad + sondeo real; la página ya no encola por su
  // cuenta. En replay sin red se lanza para conservar la MISMA operación (op.id
  // nuevo → clientOpId nuevo → accidente duplicado al siguiente drenado).
  const online = isOnlineNow();
  const reachable = online && !isReplay ? await isActuallyOnline() : online;
  if (!online || !reachable) {
    if (isReplay) throw new Error('Replay sin conexión — se reintentará');
    logMain('📴 Sin llegar al servidor — encolando accidente para sincronización posterior');
    const { redirectTo } = await queueAccidentCreate({ formData, createClientOpId: saveClientOpId });
    throw createOfflineQueuedError({ redirectTo });
  }

  // ── ONLINE: normal flow (unchanged) ──
  let orderId: string | undefined;
  const createdVehicleIds: string[] = [];
  let accidentId: string | undefined;

  const newAccidentForm = form.store.getItem(
    formAccidentNaming.NEW_ACCIDENT_FORM
  );

  const accidentDataForm = JSON.parse(
    decompressSessionStorage(newAccidentForm as string)
  );
  // Todo el paso corre bajo UN plazo (D1): al vencer, lo que esté en vuelo
  // aborta a la vez. `writesCommitted` se marca tras la última escritura.
  const deadline = createPhaseDeadline();
  const phaseClient = phaseScopedClient(client, deadline);
  let writesCommitted = false;
  try {
    const dispatch = store.dispatch;
    const cursorMultimedia = getCursor(tableIds.MULTIMEDIA)(store.getState());
    const cursorAccident = getCursor(tableIds.ACCIDENTS)(store.getState());

    const basicInfoAccidentForm = form.store.getItem(
      formAccidentNaming.ADD_BASIC_INFO_ACCIDENT_FORM
    );

    const basicInfoAccidentDataForm = JSON.parse(
      decompressSessionStorage(basicInfoAccidentForm as string)
    );

    // 🔴 Las fotos y el croquis de ESTE borrador, por nombre (revisión de la
    // Task 7; spec 2026-11-02 § 6): las carpetas `accident` y
    // `canvas-accident` de OPFS las comparten las pestañas y la edición de
    // accidentes, que desde la Task 7 ya no las vacía al guardar. Antes el
    // alta subía la carpeta `accident` ENTERA y «el primer fichero» de
    // `canvas-accident` aunque su borrador no tuviera croquis: se llevaba las
    // fotos y el croquis de otro accidente. La lista es el campo `images` de
    // la copia del paso 2; el croquis, su campo `sketch` (con `fileName`).
    const photoNames = accidentCreationPhotoNames(basicInfoAccidentDataForm);
    const sketch = accidentCreationSketch(basicInfoAccidentDataForm);

    const creationDate = new Date().toISOString();

    const damages = formData.get('damages');

    const switchValue = JSON.parse(
      formData.get('involvedVehiclesSwitch') as string
    );

    const vehicles: Vehicle[] = Object.values(
      JSON.parse(formData.get('vehicles') as string)
    );

    logMain(`📊 RESUMEN INICIAL: ${vehicles.length} vehículos a procesar`);
    logVehicles(
      '🚗 Detalle de vehículos:',
      vehicles.map((v) => v.registVehicle)
    );

    // Bloque C-8: Order paralela ya no se crea aparte.
    // SAVE_ACCIDENT (más abajo) crea Order + Accident atómicamente vía $transaction
    // Prisma — elimina cascada CREATE_NEW_ORDER + CREATE_NEW_ACCIDENT que motivaba
    // P2034 y obligaba a `skipOrderSync`. Vehicles + AttentionAccident se crean
    // ANTES para pasarse inline en el input de SAVE_ACCIDENT.

    let vehicleAndTrailerIDs;
    if (switchValue) {
      logVehicles('🚀 PROCESANDO VEHÍCULOS INVOLUCRADOS...');
      vehicleAndTrailerIDs = await Promise.all(
        vehicles.map(async (vehicle) => {
          logVehicles(`🔄 Procesando vehículo: ${vehicle.registVehicle}`);
          let dataTrailer: FetchResult | undefined = undefined;

          const dataVehicle = await phaseClient.mutate({
            mutation: FIND_OR_CREATE_VEHICLE_BY_PLATE,
            variables: {
              input: {
                plate: vehicle.registVehicle,
                // ✅ Solo datos iniciales por defecto al crear Vehicle por primera vez
                model: vehicle.modelVehicle || null,
                color: vehicle.colorVehicle || null,
                insurance: vehicle.insurerVehicle || null,
                policyNumber: vehicle.policyVehicle || null,
                isTrailer: false,
                isActive: false,
                // ✅ NUEVO: ownership y usage para accidentes
                ownership: VehicleOwnership.INTERNAL,
                usage: VehicleUsage.ACCIDENT,
              },
            },
          });

          // Trackear vehículo creado inmediatamente
          createdVehicleIds.push(
            dataVehicle.data.findOrCreateVehicleByPlate.id
          );
          logVehicles(
            `✅ Vehículo ${vehicle.registVehicle} procesado: ${dataVehicle.data.findOrCreateVehicleByPlate.id}`
          );

          // ✅ NOTA: Los contextos se crearán DESPUÉS de crear el accidente
          logVehicles(
            `✅ Vehicle entity creado, contexto se creará después del accidente`
          );

          // ❌ ELIMINADO: Contextos se crearán después de crear el accidente

          if (
            vehicle?.registTrailer &&
            (vehicle?.insurerTrailer || vehicle?.policyTrailer)
          ) {
            logVehicles(
              `🔄 Procesando trailer: ${vehicle.registTrailer} para vehículo ${vehicle.registVehicle}`
            );

            // ✅ 1. Crear/encontrar Vehicle del trailer con nueva arquitectura
            dataTrailer = await phaseClient.mutate({
              mutation: FIND_OR_CREATE_VEHICLE_BY_PLATE,
              variables: {
                input: {
                  plate: vehicle.registTrailer,
                  // ✅ Solo datos iniciales por defecto para trailer
                  insurance: vehicle.insurerTrailer || null,
                  policyNumber: vehicle.policyTrailer || null,
                  isTrailer: true,
                  isActive: false,
                  // ✅ NUEVO: ownership y usage para accidentes
                  ownership: VehicleOwnership.INTERNAL,
                  usage: VehicleUsage.ACCIDENT,
                },
              },
            });

            // Trackear trailer creado inmediatamente
            createdVehicleIds.push(
              dataTrailer.data?.findOrCreateVehicleByPlate.id
            );

            logVehicles(
              `✅ Trailer Vehicle creado: ${vehicle.registTrailer} - ID: ${dataTrailer.data?.findOrCreateVehicleByPlate.id}`
            );

            // ❌ ELIMINADO: Contexto del trailer se creará después del accidente
            logVehicles(
              `✅ Trailer Vehicle entity creado, contexto se creará después del accidente`
            );
          }

          return {
            vehicle: dataVehicle.data.findOrCreateVehicleByPlate.id,
            vehicleTrailer:
              dataTrailer?.data?.findOrCreateVehicleByPlate?.id ?? undefined,
          };
        })
      );
    }

    const dataAttentionAccident = await phaseClient.mutate({
      mutation: CREATE_NEW_ATTENTION_ACCIDENT,
      variables: {
        input: {
          // Era el ÚNICO paso no-idempotente del create: un re-replay de la
          // cola offline creaba una 2ª ficha huérfana. Con opId derivado
          // estable, el backend devuelve la ficha ya creada.
          clientOpId: `${saveClientOpId}-attention`,
          noticeConservationCenterDate:
            basicInfoAccidentDataForm?.noticeConservationCenterDate,
          arrivalConservationCenterDate:
            basicInfoAccidentDataForm?.arrivalConservationCenterDate,
          departureConservationCenterDate:
            basicInfoAccidentDataForm?.departureConservationCenterDate,
          noticeSquadBoysDate: basicInfoAccidentDataForm?.noticeSquadBoysDate,
          arrivalSquadBoysDate: basicInfoAccidentDataForm?.arrivalSquadBoysDate,
          departureSquadBoysDate:
            basicInfoAccidentDataForm?.departureSquadBoysDate,
          noticeRoadTeamDate: basicInfoAccidentDataForm.noticeRoadTeamDate,
          arrivalRoadTeamDate: basicInfoAccidentDataForm.arrivalRoadTeamDate,
          departureRoadTeamDate:
            basicInfoAccidentDataForm.departureRoadTeamDate,
          noticeControlCenterDate:
            basicInfoAccidentDataForm?.noticeControlCenterDate,
          arrivalControlCenterDate:
            basicInfoAccidentDataForm?.arrivalControlCenterDate,
          departureControlCenterDate:
            basicInfoAccidentDataForm?.departureControlCenterDate,
          noticeAmbulanceCraneAssistanceDate:
            basicInfoAccidentDataForm?.noticeAmbulanceCraneAssistanceDate,
          arrivalAmbulanceCraneAssistanceDate:
            basicInfoAccidentDataForm?.arrivalAmbulanceCraneAssistanceDate,
          departureAmbulanceCraneAssistanceDate:
            basicInfoAccidentDataForm?.departureAmbulanceCraneAssistanceDate,
          otherName: basicInfoAccidentDataForm?.otherName,
          noticeOtherDate: basicInfoAccidentDataForm?.noticeOtherDate,
          arrivalOtherDate: basicInfoAccidentDataForm?.arrivalOtherDate,
          departureOtherDate: basicInfoAccidentDataForm?.departureOtherDate,
        },
      },
    });

    const pkInitArray = accidentDataForm.pkInit.replace(/ /g, '').split('+');
    const pkEndArray = accidentDataForm.pkEnd.replace(/ /g, '').split('+');

    // FOLD contexts (At.0001979.0008.02): vehículos + snapshots del contexto
    // (modelo/color/seguro/póliza/ocupantes) + cadena de trailers viajan DENTRO
    // del saveAccident — el backend crea Vehicle↔Accident + VehicleAccidentContext
    // en su $transaction. Sustituye el loop FIND_OR_CREATE_VEHICLE_ACCIDENT_CONTEXT
    // post-save (que tocaba los contexts 2 veces y era la parte no-atómica).
    const vehiclesInput = (vehicleAndTrailerIDs || []).flatMap(
      (vehicleResponse, i) => {
        const vehicle = vehicles[i];
        if (!vehicleResponse?.vehicle) return [];
        const entries: Array<Record<string, unknown>> = [
          {
            vehicleId: vehicleResponse.vehicle,
            model: vehicle.modelVehicle || null,
            color: vehicle.colorVehicle || null,
            insurance: vehicle.insurerVehicle || null,
            policyNumber: vehicle.policyVehicle || null,
            numberOccupants: parseInt(vehicle.occupantsVehicle) || 0,
            isTrailer: false,
            isActive: false,
          },
        ];
        if (vehicleResponse.vehicleTrailer && vehicle.registTrailer) {
          entries.push({
            vehicleId: vehicleResponse.vehicleTrailer,
            insurance: vehicle.insurerTrailer || null,
            policyNumber: vehicle.policyTrailer || null,
            isTrailer: true,
            isActive: false,
            // El backend resuelve el parentVehicleContextId dentro de la tx.
            parentVehicleId: vehicleResponse.vehicle,
          });
        }
        return entries;
      }
    );

    // Bloque C-8: SAVE_ACCIDENT atómica reemplaza CREATE_NEW_ORDER + CREATE_NEW_ACCIDENT
    // (cascada legacy). Acepta inline attentionAccidentId, vehicles (con snapshots) y los
    // 6 IDs de basic-info (surveillanceBody, mediumKnowledge, weather, surfaceCondition,
    // luminosity, conditionsDriving). Backend hace ambas creaciones en un único
    // $transaction Prisma — sin riesgo P2034 y sin necesidad de skipOrderSync.
    const dataAccident = await phaseClient.mutate({
      mutation: SAVE_ACCIDENT,
      variables: {
        input: {
          clientOpId: saveClientOpId,
          parentOrderId: accidentDataForm.orderParentId as string,
          state: OrderState.Open,
          creationDate,
          registerInitDate: accidentDataForm.registerInitDate,
          pkInitKm: Number(pkInitArray[0]),
          pkInitMeter: Number(pkInitArray[1]),
          pkEndKm: Number(pkEndArray[0]),
          pkEndMeter: Number(pkEndArray[1]),
          track: accidentDataForm.track,
          direction: accidentDataForm.direction,
          margin: accidentDataForm.margin,
          lane: accidentDataForm.lane,
          concessionId: accidentDataForm.concession,
          roadId: accidentDataForm.road,
          capitolId: accidentDataForm.capitol,
          subCapitolId: accidentDataForm.subCapitol,
          classification: accidentDataForm.classification,
          ...(accidentDataForm.predefine && {
            predefinedOrderId: accidentDataForm.predefine,
          }),
          description: accidentDataForm.description,
          notes: accidentDataForm.notes,
          // Operario que registra (persona real). Obligatorio al crear en la UI;
          // el backend además lo exige para rol Tablet.
          ...(accidentDataForm.registeredByUserId && {
            registeredByUserId: accidentDataForm.registeredByUserId,
          }),
          damages,
          laneCutting: basicInfoAccidentDataForm.laneCutting,
          policeReport: basicInfoAccidentDataForm.policeReport,
          policeReportNumber: basicInfoAccidentDataForm.policeReportNumber,
          roadConditionDescription:
            basicInfoAccidentDataForm.roadConditionDescription,
          attentionAccidentId:
            dataAttentionAccident.data.createAttentionAccident.id,
          vehicles: vehiclesInput,
          surveillanceBodyId: basicInfoAccidentDataForm.surveillanceBody,
          mediumKnowledgeId: basicInfoAccidentDataForm.mediumKnowledge,
          weatherId: basicInfoAccidentDataForm.weather,
          surfaceConditionId: basicInfoAccidentDataForm.surfaceCondition,
          luminosityId: basicInfoAccidentDataForm.luminosity,
          conditionsDrivingId: basicInfoAccidentDataForm.conditionsDriving,
        },
      },
      update(cache, { data: { saveAccident } }) {
        // Cache prepend + multimedia invalidate (helper reutilizable; mismo
        // path que ejecuta el sync offline).
        prependAccidentToCache(cache, saveAccident.accident);
        // Side effect online: reset cursors para que el usuario vea el nuevo
        // accidente arriba en la lista de accidentes y multimedia. En el
        // reintento no: movería las tablas que el operario tenga abiertas.
        if (!isReplay && cursorAccident?.cursor !== undefined) {
          dispatch(setCursor(tableIds.ACCIDENTS, {
            ...cursorAccident, take: -takeBase, cursor: '',
          }));
        }
        if (!isReplay && cursorMultimedia) {
          dispatch(setCursor(tableIds.MULTIMEDIA, {
            ...cursorMultimedia, take: -takeBase, cursor: '',
          }));
        }
      },
    });

    const savedOrder = dataAccident.data.saveAccident.order;
    const savedAccident = dataAccident.data.saveAccident.accident;
    orderId = savedOrder.id;
    accidentId = savedAccident.id;
    logMain(`✅ Order + Accident creados atómicamente: order=${orderId} accident=${accidentId}`);

    // Upload images with ROID pattern (después del SAVE_ACCIDENT para usar
    // el indexExpedition/indexType/classification reales de la Order creada).
    // Offline-safe: si Cloudinary falla, encolamos el sketch en
    // `offlineImageUploadQueue` y dejamos los demás campos guardados.
    const newOrderMetadata = {
      indexExpedition: savedOrder.indexExpedition,
      indexType: savedOrder.indexType || 0,
      classification: savedOrder.classificationIDs?.[0]
        ? await getClassificationCode(savedOrder.classificationIDs[0])
        : '01',
      orderType: OrderType.Accident,
    };

    // El fichero del croquis de este borrador (`null` si no tiene): el que se
    // sube, el que copia el rescate sin red y el que se borra al terminar.
    const ownSketchFile = await draftSketchFileName(
      sketch,
      form.images,
      directoryNaming.CANVAS
    );

    let imageArray: any[] = [];
    try {
      // No pasa por Apollo: el plazo lo aplica raceWithDeadline (AbortError al vencer).
      imageArray = await raceWithDeadline(
        UploadImage(
          undefined,
          ownSketchFile ? directoryNaming.CANVAS : undefined,
          newOrderMetadata,
          {
            throwOnError: true,
            orderId: orderId!,
            entityType: 'accident',
            formImages: form.images,
            formStore: form.store,
            canvasFileName: ownSketchFile,
          },
        ),
        deadline,
      );
    } catch (uploadErr) {
      // Rescate-en-replay (bug cazado en pruebas manuales 2026-07-04, flap a
      // mitad del replay del CREAR — espejo del guard aplicado en
      // NewAction/Animals/Action/saveData.ts): en replay (opts?.clientOpId
      // presente) las imágenes YA viven en op.localImages y se restauran a
      // OPFS en cada reintento — encolar aquí el croquis en
      // offlineImageUploadQueue lo DUPLICA (una vez vía esa cola, otra vía el
      // fold de imágenes de un intento posterior). Peor: cuando ese intento
      // posterior llega, su clientOpId estable ya se consumió → el backend
      // dedupea y la FOTO normal se pierde. En replay no rescatamos —
      // dejamos que el error se propague al catch externo, que ya relanza y
      // conserva la op compound: el siguiente reintento re-sube TODO y
      // converge todo-o-nada.
      if (!opts?.clientOpId && uploadErr instanceof CloudinaryUploadError) {
        try {
          let canvasFiles: { file: File; id: string }[] = [];
          try {
            canvasFiles = await form.images.list(directoryNaming.CANVAS);
          } catch {
            canvasFiles = [];
          }
          // El de este borrador, por nombre (nunca «el primero»).
          const sourceFile = ownSketchFile
            ? pickSketchFile(canvasFiles, ownSketchFile)?.file
            : undefined;
          logMain(
            '🌐 Cloudinary failed offline — encolando sketch (sourceFile=%s)',
            sourceFile ? `${sourceFile.name} ${sourceFile.size}B` : 'NONE',
          );
          if (sourceFile && orderId) {
            const blobFileName = `pending_sketch_${orderId}_${Date.now()}.png`;
            // Directorio dedicado — sobrevive al cleanup post-save
            await saveFilesIntoADirectory(OFFLINE_PENDING_UPLOADS_DIR, [
              new File([sourceFile], blobFileName, {
                type: sourceFile.type || 'image/png',
              }),
            ]);
            const sketchData = getSketchFromSessionStorage('accident', form.store);
            const queuedId = await addImageUploadOperation({
              kind: 'sketch',
              orderId,
              orderType: OrderType.Accident,
              blobDirectory: OFFLINE_PENDING_UPLOADS_DIR,
              blobFileName,
              orderMetadata: newOrderMetadata,
              sketchData: {
                svgContent: sketchData.svgContent,
                fabricJson: sketchData.fabricJson,
              },
              // Sin sketchToRemoveId — es creación
            });
            logMain(
              '📦 sketch encolado offline (AddVehicle) id=%s blob=%s/%s',
              queuedId,
              OFFLINE_PENDING_UPLOADS_DIR,
              blobFileName,
            );
            window.dispatchEvent(
              new CustomEvent('offline-sync-error', {
                detail: { message: i18n.t('errors:sketchPendingUpload') },
              }),
            );
          }
        } catch (queueErr) {
          logMain('⚠️ Error encolando sketch offline: %O', queueErr);
        }
      } else {
        throw uploadErr;
      }
    }

    // FOLD fotos (patrón 2-save): el orderId no existe hasta crear → se sube
    // DESPUÉS del create y se referencia con un SEGUNDO saveAccident (update)
    // que crea los registros Image DENTRO de su tx (sin CREATE_NEW_IMAGE
    // suelto = sin medio-registro). Si este paso falla, el accidente YA está
    // completo (vehículos/contexts/satélites en la tx del create): NO se
    // borra nada — se avisa y se continúa (las fotos se re-añaden editando;
    // el blob de Hetzner queda como debris inocuo que limpia el cron Fase 2).
    const imagesToPersist = imageArray
      .filter((img) => !!img.storageKey)
      .map((img) => ({
        storageKey: img.storageKey,
        name: img.name,
        creatingDate: img.creatingDate,
        sketchSvg: img.sketchSvg ?? null,
        sketchFabricJson: img.sketchFabricJson ?? null,
      }));
    if (imagesToPersist.length > 0) {
      try {
        await phaseClient.mutate({
          mutation: SAVE_ACCIDENT,
          variables: {
            input: {
              // Derivado estable del opId principal → el replay tampoco
              // duplica las fotos.
              clientOpId: `${saveClientOpId}-images`,
              id: accidentId,
              images: imagesToPersist,
            },
          },
        });
        logMain(`✅ ${imagesToPersist.length} imágenes registradas vía saveAccident (tx)`);
      } catch (imagesErr) {
        // Transporte (plazo vencido, red caída): sube al catch exterior, que
        // encola la creación entera con el mismo clientOpId; el replay repite
        // este update con `-images` sin duplicar (spec § 0.4).
        if (isTransportFailure(imagesErr)) throw imagesErr;
        const queued =
          (imagesErr as { offlineQueued?: boolean })?.offlineQueued === true ||
          (
            imagesErr as { networkError?: { offlineQueued?: boolean } }
          )?.networkError?.offlineQueued === true ||
          ((imagesErr as { message?: string })?.message ?? '').includes(
            'Mutation queued for offline sync'
          );
        if (queued) {
          // Se cayó la red entre el save #1 y el #2: OfflineQueueLink encoló el
          // saveAccident de imágenes. NO es un fallo — al reconectar se replay-ea
          // con su clientOpId derivado (`${opId}-images`) sin duplicar.
          logMain('📴 saveAccident(images) encolado offline — sincronizará al reconectar');
        } else {
          Sentry.captureException(imagesErr);
          logError('⚠️ Accidente creado pero fotos NO registradas:', imagesErr);
          window.dispatchEvent(
            new CustomEvent('offline-sync-error', {
              detail: { message: i18n.t('errors:uploadingImages') },
            }),
          );
        }
      }
    }

    // Fotos al final (spec 2026-09-08-fase2-encolada § 2 D4): ÚLTIMA escritura
    // del paso. Antes del plazo → el snapshot de la cola se las lleva; después
    // → no hay replay. Como va detrás del saveAccident, no choca con su tx.
    // `directoryNaming.CANVAS` vale 'canvas-accident' (constante de
    // AddBasicInfoAccidentForm); el croquis sigue subiéndose de forma bloqueante.
    await enqueuePhotosForUpload({
      picturesDirectoryName: OrderType.Accident,
      orderId: orderId!,
      orderType: OrderType.Accident,
      entityType: 'accident',
      orderMetadata: newOrderMetadata,
      // 🔴 Solo las que lista el borrador (ver `photoNames` arriba).
      onlyNames: photoNames,
      images: form.images,
      handoff: form.photoHandoff,
    });
    writesCommitted = true;

    // FOLD contexts: el loop FIND_OR_CREATE_VEHICLE_ACCIDENT_CONTEXT post-save
    // ELIMINADO — los contexts (con snapshots + cadena de trailers) los crea el
    // backend dentro de la tx del saveAccident (input.vehicles, arriba).
    logContext('✅ VehicleAccidentContext creados por el backend dentro de la tx');

    const unresolvedPromiseVehicles: Promise<ApolloQueryResult<object>>[] = [];

    (vehicleAndTrailerIDs || []).forEach((vehicleAndTrailerId) => {
      unresolvedPromiseVehicles.push(
        phaseClient.query({
          fetchPolicy: 'network-only',
          query: GET_VEHICLE_BY_ID,
          variables: { id: vehicleAndTrailerId.vehicle },
        })
      );

      if (vehicleAndTrailerId.vehicleTrailer) {
        unresolvedPromiseVehicles.push(
          phaseClient.query({
            fetchPolicy: 'network-only',
            query: GET_VEHICLE_BY_ID,
            variables: { id: vehicleAndTrailerId.vehicleTrailer },
          })
        );
      }
    });

    // Solo calientan la caché: si el plazo vence aquí, todo está escrito.
    try {
      await Promise.all([
        phaseClient.query({
          fetchPolicy: 'network-only',
          query: GET_ORDER_BY_ID,
          variables: { id: orderId },
        }),
        // ✅ Refresh vehicle contexts to update UI (consistente con EditAccident)
        client
          .query({
            fetchPolicy: 'network-only',
            query: GET_VEHICLE_CONTEXTS_BY_ACCIDENT,
            variables: { accidentId: accidentId },
          })
          .then((result) => {
            logMain(
              '✅ GET_VEHICLE_CONTEXTS_BY_ACCIDENT refrescado correctamente'
            );
            logMain(
              `📋 Contextos obtenidos: ${
                result.data.vehicleContextsByAccident?.length || 0
              } contextos`
            );
          }),
        // Refresh accident data to update UI
        phaseClient.query({
          fetchPolicy: 'network-only',
          query: GET_ACCIDENT_BY_ID,
          variables: { id: accidentId },
        }),
        ...unresolvedPromiseVehicles,
        phaseClient.query({
          fetchPolicy: 'network-only',
          query: GET_ATTENTION_ACCIDENT_BY_ID,
          variables: {
            id: dataAttentionAccident.data.createAttentionAccident.id,
          },
        }),
      ]);
    } catch (refreshErr) {
      if (!isTransportFailure(refreshErr)) throw refreshErr;
    }

    // Bloque C-5.4: el bloque UPDATE_ORDER + UPDATE_ORDER_RELATIONSHIPS post-create
    // (con skipOrderSync) era redundante — CREATE_NEW_ACCIDENT ya sincroniza los
    // Tipo B (concession/road/PK) a la Order paralela. Eliminado para simplificar
    // y consistencia con C-2/C-3.

    // Solo lo de este alta: sus copias, sus fotos y su croquis, por nombre.
    // Ni la edición de accidente a medias ni las carpetas compartidas (Ruling
    // 5; antes `removeNewAccidentStorage` vaciaba las dos carpetas).
    await releaseAccidentCreationDraft(form, accidentDataForm.orderParentId, {
      sketchFileName: ownSketchFile,
    });

    // Store navigation context for summary page redirect (after cleanup to avoid being removed)
    if (accidentDataForm.orderParentId) {
      logMain(
        `📍 Storing navigation context: origin=workorder, parentOrderId=${accidentDataForm.orderParentId}, orderParentRoid=${accidentDataForm.orderParentRoid}, isCampaignParent=${accidentDataForm.isCampaignParent}`
      );
      form.store.setItem(
        formAccidentNaming.ACCIDENT_NAVIGATION_CONTEXT,
        compressSessionStorage(
          JSON.stringify({
            origin: 'workorder',
            parentOrderId: accidentDataForm.orderParentId,
            parentRoid: accidentDataForm.orderParentRoid,
            isCampaignParent: accidentDataForm.isCampaignParent,
          })
        )
      );
    }

    logMain('🎉 PROCESO DE CREACIÓN DE ACCIDENTE COMPLETADO EXITOSAMENTE');
    logMain('✅ RESUMEN FINAL DE NUEVAS ARQUITECTURA:');
    logMain(`📋 Vehículos principales procesados: ${vehicles.length}`);
    logMain(`📋 Total Vehicle entities creadas: ${createdVehicleIds.length}`);
    logMain(`📋 VehicleAccidentContext creados durante procesamiento`);
    logMain(`📂 Redirigiendo a: accidents/${orderId}`);

    return redirect(
      `${
        accidentRouteInner === AccidentRoute.CAMPAIGN ? '../' : ''
      }../../../accidents/${orderId}`
    );
  } catch (error) {
    if (!isReplay && isTransportFailure(error) && !writesCommitted) {
      // Red lenta o cortada antes de completar: a la cola con el MISMO
      // clientOpId (spec D3). Si el create llegó, el replay recibe el accidente
      // ya creado; si no, lo crea. Nunca dos.
      console.warn('[offline] accidente sin llegar al servidor; encolado', error);
      const { redirectTo } = await queueAccidentCreate({ formData, createClientOpId: saveClientOpId });
      throw createOfflineQueuedError({ redirectTo });
    }
    // El rollback compensatorio antiguo (borrar imágenes + contexts + Order)
    // se ELIMINÓ: con el fold, el saveAccident es atómico (o crea TODO —
    // Order+Accident+vehículos+contexts+satélites — o no crea NADA), así que
    // no hay estado parcial que limpiar. Peor aún, aquel rollback podía BORRAR
    // un accidente completo y válido si fallaba un paso posterior (pérdida de
    // datos silenciosa, clase del incidente At.0001979.0008.02). Los pasos
    // post-save (fotos) manejan su fallo sin abortar (ver arriba).
    Sentry.captureException(error);
    logError('❌ Error guardando accidente:', error);
    // Re-lanzar: el errorElement de la ruta muestra el fallo. Antes: `return
    // null` → el usuario se quedaba sin feedback y sin saber si se guardó.
    throw error;
  } finally {
    deadline.clear();
  }
};
